/**
 * Transporte HTTP de eficiencia reproductiva de cría.
 *
 * Contrato: Tambo-Back `docs/cria/openapi_cria.json` (fase-7, 17 operaciones)
 * más las capturas existentes de servicio, tacto, parto y destete.
 * Guía: Tambo-Back `docs/cria/FRONTEND_INTEGRATION.md`.
 *
 * Reglas:
 * - Ninguna ruta acepta `tambo_id`: el backend usa el establecimiento de la sesión.
 * - Las listas en query se envían repitiendo el parámetro
 *   (`campania_ids=1&campania_ids=2`), no con corchetes.
 * - Cada función devuelve `data` y acepta `{ signal }` para cancelar.
 */
import apiTamboUrl from "@/services/api";

/** Serializa query con parámetros repetidos y descarta vacíos (undefined, null, ""). */
export function serializarQuery(params = {}) {
  const q = new URLSearchParams();
  for (const [clave, valor] of Object.entries(params || {})) {
    if (valor === undefined || valor === null || valor === "") continue;
    const valores = Array.isArray(valor) ? valor : [valor];
    for (const item of valores) {
      if (item === undefined || item === null || item === "") continue;
      q.append(clave, String(item));
    }
  }
  return q.toString();
}

function conQuery(ruta, params) {
  const q = serializarQuery(params);
  return q ? `${ruta}?${q}` : ruta;
}

async function get(ruta, params, { signal } = {}) {
  const { data } = await apiTamboUrl.get(conQuery(ruta, params), { signal });
  return data;
}

async function post(ruta, cuerpo, { signal } = {}) {
  const { data } = await apiTamboUrl.post(ruta, cuerpo, { signal });
  return data;
}

async function patch(ruta, cuerpo, { signal } = {}) {
  const { data } = await apiTamboUrl.patch(ruta, cuerpo, { signal });
  return data;
}

export const criaEficienciaApi = {
  // ── Campañas ──
  listarCampanias: (params, opts) => get("/cria/campanias", params, opts),
  crearCampania: (cuerpo, opts) => post("/cria/campanias", cuerpo, opts),
  obtenerCampania: (id, opts) => get(`/cria/campanias/${id}`, null, opts),
  editarCampania: (id, cuerpo, opts) =>
    patch(`/cria/campanias/${id}`, cuerpo, opts),
  cerrarCampania: (id, cuerpo, opts) =>
    post(`/cria/campanias/${id}/cerrar`, cuerpo, opts),
  reabrirCampania: (id, cuerpo, opts) =>
    post(`/cria/campanias/${id}/reabrir`, cuerpo, opts),
  obtenerIndicadores: (id, params, opts) =>
    get(`/cria/campanias/${id}/indicadores`, params, opts),

  // ── Vientres ──
  listarVientres: (id, params, opts) =>
    get(`/cria/campanias/${id}/vientres`, params, opts),
  inscribirVientres: (id, cuerpo, opts) =>
    post(`/cria/campanias/${id}/vientres`, cuerpo, opts),
  corregirVientre: (id, animalId, cuerpo, opts) =>
    patch(`/cria/campanias/${id}/vientres/${animalId}`, cuerpo, opts),

  // ── Superficies ──
  listarSuperficies: (id, opts) =>
    get(`/cria/campanias/${id}/superficies`, null, opts),
  agregarSuperficie: (id, cuerpo, opts) =>
    post(`/cria/campanias/${id}/superficies`, cuerpo, opts),

  // ── Eficiencia individual ──
  obtenerEficienciaAnimal: (animalId, params, opts) =>
    get(`/cria/animales/${animalId}/eficiencia`, params, opts),
  registrarPesoDestete: (animalId, cuerpo, opts) =>
    post(`/cria/animales/${animalId}/peso-destete`, cuerpo, opts),

  // ── Rankings, comparación y calidad ──
  obtenerRanking: (params, opts) => get("/cria/rankings", params, opts),
  obtenerComparacion: (params, opts) => get("/cria/comparacion", params, opts),
  obtenerCalidadDatos: (params, opts) =>
    get("/cria/calidad-datos", params, opts),

  // ── Capturas existentes (no están detrás del interruptor) ──
  registrarServicio: (animalId, cuerpo, opts) =>
    post(`/cria/registrar-servicio/${animalId}`, cuerpo, opts),
  registrarTacto: (animalId, cuerpo, opts) =>
    post(`/cria/registrar-tacto/${animalId}`, cuerpo, opts),
  registrarParto: (animalId, cuerpo, opts) =>
    post(`/cria/registrar-parto/${animalId}`, cuerpo, opts),
  registrarDestete: (animalId, cuerpo, opts) =>
    post(`/feedlot/destete/${animalId}`, cuerpo, opts),
};

export default criaEficienciaApi;
